'use client'

import React from "react"

import { useState, useEffect } from 'react'
import { DashboardLayout } from '@/components/dashboard-layout'
import { Badge } from '@/components/badge'
import { Plus, Settings, X } from 'lucide-react'
import { Template } from '@/lib/types'

function TemplateCard({ template }: { template: Template }) {
  const [showDeployForm, setShowDeployForm] = useState(false)
  const [deployConfig, setDeployConfig] = useState({
    targetHost: 'x-node-ny-01',
    networkMode: 'bridge',
  })

  const categoryColors: Record<string, string> = {
    'Honeypot Templates': 'bg-blue-500/20 text-blue-400',
    'Vulnerable Lab Templates': 'bg-red-500/20 text-red-400',
    'Deception Infra Templates': 'bg-cyan-500/20 text-cyan-400',
  }

  const handleDeployChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target
    setDeployConfig((prev) => ({ ...prev, [name]: value }))
  }

  const handleCloseForm = () => {
    setShowDeployForm(false)
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden hover:border-primary/50 transition-all hover:shadow-lg">
      <div className="p-6">
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-lg font-semibold text-foreground flex-1">{template.name}</h3>
          <Badge className={categoryColors[template.category]}>{template.category}</Badge>
        </div>

        <div className="mb-4">
          {template.docker_image && (
            <Badge variant="default" className="bg-secondary/50 text-foreground mb-3">
              {template.docker_image}
            </Badge>
          )}
          <p className="text-sm text-muted-foreground">{template.usage}</p>
          {template.tags && template.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {template.tags.map((tag) => (
                <Badge key={tag} variant="outline" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-border">
          <span className="text-xs text-muted-foreground">
            {template.ports && template.ports.length > 0 ? `Ports: ${template.ports.join(', ')}` : 'No ports configured'}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setShowDeployForm(!showDeployForm)}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 transition-opacity"
            >
              Deploy Now
            </button>
            <button className="p-2 hover:bg-secondary rounded transition-colors">
              <Settings size={16} className="text-muted-foreground" />
            </button>
          </div>
        </div>
      </div>

      {/* Inline Deploy Form */}
      {showDeployForm && (
        <div className="border-t border-border bg-secondary/20 p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-foreground">Deployment Configuration</h4>
            <button onClick={handleCloseForm} className="p-2 hover:bg-secondary rounded transition-colors">
              <X size={16} className="text-foreground" />
            </button>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-2">TARGET HOST</label>
                <select
                  name="targetHost"
                  value={deployConfig.targetHost}
                  onChange={handleDeployChange}
                  className="w-full px-3 py-2 bg-card border border-border rounded text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="x-node-ny-01">X-Node-NY-01</option>
                  <option value="x-node-ln-02">X-Node-LN-02</option>
                  <option value="x-node-sg-03">X-Node-SG-03</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-2">NETWORK MODE</label>
                <select
                  name="networkMode"
                  value={deployConfig.networkMode}
                  onChange={handleDeployChange}
                  className="w-full px-3 py-2 bg-card border border-border rounded text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="bridge">Bridge</option>
                  <option value="host">Host</option>
                  <option value="overlay">Overlay</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-2">PORTS</label>
              <div className="px-3 py-2 bg-card border border-border rounded text-sm text-foreground font-mono">
                22, 2222 (BRIDGE)
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button className="px-4 py-2 bg-primary text-primary-foreground rounded font-medium text-sm hover:opacity-90 transition-opacity">
                Deploy
              </button>
              <button
                onClick={handleCloseForm}
                className="px-4 py-2 border border-border text-foreground rounded font-medium text-sm hover:bg-secondary/20 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default function TemplatesPage() {
  const [templates, setTemplates] = useState<Template[]>([])
  const [activeTab, setActiveTab] = useState('All')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCustomModal, setShowCustomModal] = useState(false)
  const [customTemplate, setCustomTemplate] = useState({
    name: '',
    baseTemplate: 'tpot',
  })

  const categoryTabs = ['All', 'Honeypot Templates', 'Vulnerable Lab Templates', 'Deception Infra Templates']

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true)
        setError(null)
        const response = await fetch('/api/templates')
        if (response.ok) {
          const data = await response.json()
          if (Array.isArray(data) && data.length > 0) {
            setTemplates(data)
          } else {
            setError('No templates found')
          }
        } else {
          setError('Failed to fetch templates')
        }
      } catch (error) {
        setError('Error loading templates')
        console.error('Error fetching templates:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchTemplates()
  }, [])

  const filteredTemplates = templates.filter((template) => {
    if (activeTab === 'All') return true
    return template.category === activeTab
  })

  const handleCustomChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setCustomTemplate((prev) => ({ ...prev, [name]: value }))
  }

  const handleSaveTemplate = () => {
    console.log('[v0] Template saved:', customTemplate)
    setShowCustomModal(false)
    setCustomTemplate({ name: '', baseTemplate: 'tpot' })
  }

  const handleCloseModal = () => {
    setShowCustomModal(false)
    setCustomTemplate({ name: '', baseTemplate: 'tpot' })
  }

  const yamlPreview = `version: '3.8'
services:
  honeypot:
    image: ${customTemplate.baseTemplate === 'tpot' ? 't-pot/all' : customTemplate.baseTemplate === 'qeeqbox' ? 'qeeqbox/qeeqbox' : customTemplate.baseTemplate === 'vulhub' ? 'vulhub/base' : 'honeypot/custom'}
    ports:
      - "22:22"
      - "8080:8080"
    networks:
      - honeypot-net
    environment:
      - HONEYPOT_NAME=${customTemplate.name || 'My Honeypot'}
      - ENABLED_SERVICES=ssh,http,ftp

networks:
  honeypot-net:
    driver: bridge`

  return (
    <DashboardLayout
      title="Template Browser"
      subtitle="One-click deployments from trusted community sources."
      actions={
        <button
          onClick={() => setShowCustomModal(true)}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center gap-2"
        >
          <Plus size={18} />
          Custom Template
        </button>
      }
    >
      <div className="space-y-6">
        {/* Filter Tabs */}
        <div className="flex gap-2 border-b border-border pb-4">
          {categoryTabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 font-medium transition-colors ${
                activeTab === tab
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Templates Grid */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Loading templates...</p>
          </div>
        ) : error ? (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6 text-center">
            <p className="text-red-400 mb-2">{error}</p>
            <p className="text-sm text-muted-foreground">Please check your Supabase configuration or ensure templates.json exists.</p>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No templates found for this category.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTemplates.map((template) => (
              <TemplateCard key={template.id} template={template} />
            ))}
          </div>
        )}

        {/* Custom Template Modal */}
        {showCustomModal && (
          <>
            {/* Overlay */}
            <div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
              onClick={handleCloseModal}
            />

            {/* Modal */}
            <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl bg-card border border-border rounded-lg p-8 z-50 shadow-2xl">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-foreground">Create Custom Template</h2>
                <button onClick={handleCloseModal} className="p-2 hover:bg-secondary rounded transition-colors">
                  <X size={24} className="text-foreground" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Template Name */}
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">Template Name</label>
                  <input
                    type="text"
                    name="name"
                    value={customTemplate.name}
                    onChange={handleCustomChange}
                    placeholder="e.g., My Custom SSH Honeypot"
                    className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                {/* Base Template */}
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">Base Template</label>
                  <select
                    name="baseTemplate"
                    value={customTemplate.baseTemplate}
                    onChange={handleCustomChange}
                    className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="tpot">T-Pot</option>
                    <option value="qeeqbox">qeeqbox</option>
                    <option value="vulhub">Vulhub</option>
                    <option value="empty">Empty</option>
                  </select>
                </div>

                {/* YAML Preview */}
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">YAML Preview (Read-Only)</label>
                  <pre className="w-full px-4 py-3 bg-secondary border border-border rounded-lg text-foreground text-xs font-mono overflow-x-auto max-h-48">
                    {yamlPreview}
                  </pre>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t border-border">
                  <button
                    onClick={handleSaveTemplate}
                    className="px-6 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 transition-opacity"
                  >
                    Save Template
                  </button>
                  <button
                    onClick={handleCloseModal}
                    className="px-6 py-2 border border-border text-foreground rounded-lg font-medium hover:bg-secondary/20 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  )
}
